/**
 * Per-user, per-device "seen" state for the home Últimas transações feed —
 * what powers the Novo chip's lifecycle:
 *
 * - a WATERMARK (ISO ingestion time): everything created up to it has been
 *   shown to the user. The widget advances it when the feed renders, so a
 *   flagged row is Novo exactly once — the next open comes clean.
 * - a SEEN-ID set: rows the user explicitly handled before the watermark
 *   caught up (reviewed via the Revisar flow, or opened in the detail
 *   drawer) — those must never flag, even on the first open after a sync.
 *
 * localStorage-backed (deliberately device-local, like the privacy toggle);
 * all functions are safe with storage unavailable (SSR/incognito).
 */

export interface HomeFeedSeenState {
  /** Newest ingestion stamp already shown to the user, or null on first visit. */
  watermark: string | null;
  seenIds: ReadonlySet<string>;
}

interface StoredShape {
  w?: string | null;
  ids?: string[];
}

const MAX_SEEN_IDS = 200;

function keyFor(userId: string): string {
  return `vertice:home-feed-seen:${userId}`;
}

function readStored(userId: string): StoredShape {
  try {
    const raw = window.localStorage.getItem(keyFor(userId));
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? (parsed as StoredShape) : {};
  } catch {
    return {};
  }
}

function writeStored(userId: string, value: StoredShape): void {
  try {
    window.localStorage.setItem(keyFor(userId), JSON.stringify(value));
  } catch {
    // Storage unavailable — the chip just stays session-only.
  }
}

export function readHomeFeedSeen(userId: string): HomeFeedSeenState {
  const stored = readStored(userId);
  return {
    watermark: typeof stored.w === 'string' ? stored.w : null,
    seenIds: new Set(Array.isArray(stored.ids) ? stored.ids.filter((id) => typeof id === 'string') : []),
  };
}

/**
 * The page-view snapshot: the home renders TWO instances of the feed widget
 * (mobile + desktop trees) and StrictMode re-runs effects — whoever reads
 * first must not spoil the state for the others by advancing the watermark.
 * All reads within the window share the first (clean) snapshot; a later
 * visit re-reads storage and comes clean.
 */
let snapshotCache: { userId: string; state: HomeFeedSeenState; at: number } | null = null;
const SNAPSHOT_WINDOW_MS = 30_000;

export function readHomeFeedSeenSnapshot(userId: string): HomeFeedSeenState {
  const now = Date.now();
  if (
    !snapshotCache ||
    snapshotCache.userId !== userId ||
    now - snapshotCache.at > SNAPSHOT_WINDOW_MS
  ) {
    snapshotCache = { userId, state: readHomeFeedSeen(userId), at: now };
  }
  return snapshotCache.state;
}

/** Test seam — the snapshot is module state and would leak between specs. */
export function resetHomeFeedSeenSnapshot(): void {
  snapshotCache = null;
}

/** Advance-only: an older stamp never regresses the watermark. Stamps are
 * compared NUMERICALLY (Date.parse) — the serialization offset (-03:00 vs Z)
 * is an environment detail, so lexicographic ordering must never decide. */
export function advanceHomeFeedWatermark(userId: string, isoStamp: string): void {
  const incoming = Date.parse(isoStamp);
  if (!Number.isFinite(incoming)) return;
  const stored = readStored(userId);
  const current = typeof stored.w === 'string' ? Date.parse(stored.w) : NaN;
  if (Number.isFinite(current) && current >= incoming) return;
  writeStored(userId, { ...stored, w: isoStamp });
}

/** Mark rows the user explicitly handled (review confirm / detail open). */
export function markHomeFeedSeenIds(userId: string, ids: readonly string[]): void {
  if (ids.length === 0) return;
  const stored = readStored(userId);
  const merged = [...new Set([...(stored.ids ?? []), ...ids])];
  // Cap from the FRONT (oldest entries drop): ids older than the watermark
  // stop mattering anyway once the user opens the feed.
  writeStored(userId, { ...stored, ids: merged.slice(-MAX_SEEN_IDS) });
  // A fresh explicit signal must reach the NEXT view even inside the snapshot
  // window — drop the cached page-view snapshot so it re-reads storage.
  snapshotCache = null;
}
